import { useEffect, useState } from 'react';
import { Input } from 'antd';
import cn from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import Wrapper from './image_library_page.styled';
import { ImagesGallery } from './components/image_gallery';
import { ImageLibraryButtons } from './components/image_buttons';
import { UploadImagesModal } from './modals/upload_images_modal';
import { S3ApiRepository } from '../../data/repositories/s3_repository';
import { AuthenticatedApiRepository } from '../../data/repositories/authenticated_base_repository';
import { SilalImagesResponse } from '../../data/repositories/shared_repository';
import { ImageType } from '../../data/types/files';
import { toast } from 'react-toastify';
import {
  actionGetSilalImages,
  GET_SILAL_IMAGES,
  GET_STORE_IMAGES,
} from '../../store/images/image_library_actions';
import { shouldRefreshCache } from '../../utils/functions/caching_functions';
import { Helmet } from 'react-helmet-async';

export enum ImageLibraryType {
  SilalImages = 'silal_images',
  StoreImages = 'store_images',
}

type ImageLibraryPageProps = {
  actionGetStoreImages: any;
  filesRepository: AuthenticatedApiRepository;
  s3Repository: S3ApiRepository;
  store_id?: string;
  is_private?: boolean;
};

const ImageLibraryPage = ({
  actionGetStoreImages,
  filesRepository,
  s3Repository,
  store_id,
  is_private,
}: ImageLibraryPageProps) => {
  const dispatch = useDispatch();
  const [silalImages, setSilalImages] = useState<ImageType[]>([]);
  const [storeImages, setStoreImages] = useState<ImageType[]>([]);

  const [activeLibrary, setActiveLibrary] = useState(
    store_id ? ImageLibraryType.StoreImages : ImageLibraryType.SilalImages,
  );
  const [alts_modal, setAltsModal] = useState(false);
  const [searchFocus] = useState(false);
  const [search, setSearch] = useState('');
  const [edit_able_images, setEditableImages] = useState<ImageType[]>([]);
  const [done, setDone] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(101);
  const [prev_page, setPrevPage] = useState(1);

  const {
    silal_images,
    store_images,
    lastFetchedSilalImages,
    lastFetchedStoreImages,
  } = useSelector((state: any) => state.Images);

  type GetImagesAction = ReturnType<typeof actionGetStoreImages>;
  type GetSilalImagesAction = ReturnType<typeof actionGetSilalImages>;

  const loadMore = () => {
    getData(page + 1, true);
    setPage(page + 1);
  };

  useEffect(() => {
    if (activeLibrary === ImageLibraryType.SilalImages) {
      setStoreImages([]);
    } else {
      setSilalImages([]);
    }
    getData(1, true);
    setPage(1);
    setTotal(101);
  }, [activeLibrary]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      getData(1, true);
      setPage(1);
      setTotal(101);
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [search]);

  useEffect(() => {
    getData(1);
  }, [activeLibrary, lastFetchedSilalImages, lastFetchedStoreImages]);

  const getData = async (page: number, skipCacheCheck: boolean = false) => {
    function onSuccess(results: SilalImagesResponse | any) {
      if (!results) {
        toast.warn('Maximum number of images reached');
        return;
      }

      if (prev_page === results.page) {
        if (results.files) {
          setStoreImages(results.files);
        } else {
          setSilalImages(results.images);
        }

        return;
      } else if (page === results.page) {
        if (results.files) {
          setStoreImages(storeImages.concat(results.files));
        } else {
          setSilalImages(silalImages.concat(results.images));
        }
        setPrevPage(results.page);
      }
      if (results.files || results.images) {
        setTotal(results.total || 101);
      }
    }
    const payload = {
      data: {
        limit: 100,
        page: page,
        query: `*${search}*` || '*',
        is_silal_owned: true,
      },
      success: onSuccess,
    };

    if (activeLibrary === ImageLibraryType.SilalImages) {
      if (
        skipCacheCheck ||
        !lastFetchedSilalImages ||
        shouldRefreshCache(GET_SILAL_IMAGES, lastFetchedSilalImages)
      ) {
        dispatch(actionGetSilalImages(payload) as GetSilalImagesAction);
      } else {
        onSuccess(silal_images); // Use the cached data if available
      }
    }

    if (activeLibrary === ImageLibraryType.StoreImages) {
      if (
        skipCacheCheck ||
        !lastFetchedStoreImages ||
        shouldRefreshCache(GET_STORE_IMAGES, lastFetchedStoreImages)
      ) {
        payload.data.is_silal_owned = false;
        dispatch(actionGetStoreImages(payload) as GetImagesAction);
      } else {
        onSuccess(store_images); // Use the cached data if available
      }
    }
  };

  return (
    <Wrapper>
      <Helmet>
        <title>Image Library | Silal Management</title>
      </Helmet>

      <div className="top-row">
        <div className="d-flex">
          {/* <img
              src={backarrow}
              // onClick={() => navigate(-1)}
              alt="arrow"
              width={25}
              style={{ cursor: 'pointer' }}
            /> */}
          <h2
            style={{
              marginLeft: '20px',
              marginTop: '20px',
              fontSize: '25px',
              fontWeight: '700',
            }}
          >
            Image Library
          </h2>
        </div>
      </div>
      <div className="d-flex">
        <div className="flex-1 p-3" style={{ width: '100%' }}>
          <div className="row">
            <div>
              <div>
                <div className="Silal-library-div">
                  <div>
                    {store_id === undefined ? (
                      <button
                        className={
                          activeLibrary === ImageLibraryType.SilalImages
                            ? 'active'
                            : ''
                        }
                        onClick={() =>
                          setActiveLibrary(ImageLibraryType.SilalImages)
                        }
                      >
                        Silal Images
                      </button>
                    ) : (
                      <>
                        <button
                          className={
                            activeLibrary === ImageLibraryType.StoreImages
                              ? 'active'
                              : ''
                          }
                          onClick={() => {
                            setActiveLibrary(ImageLibraryType.StoreImages);
                          }}
                          style={{}}
                        >
                          Store Images
                        </button>
                        <button
                          className={
                            activeLibrary === ImageLibraryType.SilalImages
                              ? 'active'
                              : ''
                          }
                          onClick={() => {
                            setActiveLibrary(ImageLibraryType.SilalImages);
                          }}
                          style={{}}
                        >
                          Silal Images
                        </button>
                      </>
                    )}
                    {/* <button // TODO uncomment this when unsplash is ready
                    className={
                      activeLibrary === "unsplash_images" ? "active" : ""
                    }
                    onClick={() => setActiveLibrary("unsplash_images")}
                    text="Unsplash"
                    style={{}}
                  >
                    Unsplash
                  </button> */}
                  </div>
                </div>
              </div>
            </div>
            <div>
              {(activeLibrary === ImageLibraryType.StoreImages ||
                !store_id) && (
                <div style={{ textAlign: 'end' }}>
                  <UploadImagesModal
                    postSuccessfullUploadCallback={() => getData(1, true)}
                    getData={() => getData(page, true)}
                    store_id={store_id}
                    is_private={is_private}
                    filesRepository={filesRepository}
                    s3Repository={s3Repository}
                  />
                </div>
              )}
            </div>
          </div>

          <div>
            <div>
              <div
                className={cn(
                  'searchContainer top-row',
                  searchFocus && 'add-searchbutton',
                )}
              >
                <Input
                  placeholder="Search by keywords or alts"
                  value={search}
                  onChange={(e: any) =>
                    setSearch((e.target as HTMLInputElement).value)
                  }
                />
                <div>
                  <ImageLibraryButtons
                    filesRepository={filesRepository}
                    alts_modal={alts_modal}
                    toggleAltsModal={() => setAltsModal(!alts_modal)}
                    edit_able_images={edit_able_images}
                    toggleDone={() => setDone(!done)}
                    getData={() => getData(1, true)}
                    store_id={store_id}
                    images={
                      activeLibrary === ImageLibraryType.StoreImages
                        ? storeImages
                        : silalImages
                    }
                    setImages={
                      activeLibrary === ImageLibraryType.StoreImages
                        ? setStoreImages
                        : setSilalImages
                    }
                    setEditableImages={setEditableImages}
                    ownedLibrary={
                      activeLibrary === ImageLibraryType.StoreImages ||
                      !store_id
                    }
                  />
                </div>
              </div>
              <ImagesGallery
                alts_modal={alts_modal}
                images={
                  activeLibrary === ImageLibraryType.StoreImages
                    ? storeImages
                    : silalImages
                }
                loadMore={loadMore}
                total={total}
                setEditableImages={setEditableImages}
                done={done}
                search={search}
                inCreateListing={false}
                ownedLibrary={
                  activeLibrary === ImageLibraryType.StoreImages || !store_id
                }
              />
            </div>
          </div>
        </div>
      </div>
    </Wrapper>
  );
};

export default ImageLibraryPage;
